import { SvgTune } from '@loomhq/lens';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  endThumbnailFlow,
  endVideoSettingsFlow,
  startVideoSettingsFlow,
} from 'actions/editor';
import {
  requestVideoPlayerLeaveFullScreen,
  updateShowWeaveEditTools,
  updateTheaterMode,
} from 'actions/videoPlayer';
import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import { ROLLOUT_WEAVE, VARIANT } from 'constants/featureFlag';
import { VIDEO_ENTER_TRIM_MODE } from 'constants/hotkeys';
import { V3, V4, V5 } from 'constants/recordingVersions';
import {
  VIDEO_EDIT_OPTIONS_ACCESS,
  VIDEO_SETTINGS_ACCESS,
} from 'constants/scopes';
import { clearCTAWidgetState } from 'creators/editor';
import { handleToggleTranscriptCreator } from 'creators/shareVideo';
import {
  selectCtaFlow,
  selectThumbnailFlow,
  selectVideoSettingsFlow,
} from 'selectors/editor';
import {
  selectFullscreen,
  selectRecordingVersion,
  selectTranscriptVisible,
} from 'selectors/videoPlayer';
import { selectIsActive } from 'selectors/viewerInsights';

import { useFeatureFlagValue } from 'hooks/featureFlag';

import AddVideoCtaButton from '../add-video-cta-button';
import CollapsibleSideButton from '../collapsible-side-button';
import CustomThumbnailButton from '../custom-thumbnail-button';
import TranscriptOverlayButton from '../transcript-overlay-button';
import TrimModeButton from '../trim-mode-button';
import VideoButtons from '../video-buttons';

import Scopes from 'components/scopes';

const EditVideoButtons = ({
  // collapsed = only icons are showing, text is hidden
  // collapsible = can change state into collapsed state (false = force uncollapsed)
  // hidden = this component renders nothing
  isCollapsible = false,
  isHidden,
  onEnterTrimMode,
  startCollapsed = false,
  waitingForTrim = false,
}) => {
  const dispatch = useDispatch();

  const areViewerInsightsOpen = useSelector(selectIsActive);
  const isVideoPlayerFullScreen = useSelector(selectFullscreen);
  const areThumbnailSettingsOpen = useSelector(selectThumbnailFlow);
  const areVideoCtaSettingsOpen = useSelector(selectCtaFlow);
  const areVideoSettingsOpen = useSelector(selectVideoSettingsFlow);
  const isTranscriptVisible = useSelector(selectTranscriptVisible);

  const recordingVersion = useSelector(selectRecordingVersion);

  const weaveFlagValue = useFeatureFlagValue(ROLLOUT_WEAVE);
  const rolloutWeave = VARIANT.includes(weaveFlagValue);

  const confirmAddClips = () => {
    const confirmAddClipsTitle =
      'WARNING: As an early version of this feature, please avoid using Add Clips for external facing videos until our public release.\n\nIf you click OK, the video will be automatically converted to a weaved video and viewers may not have the ability to download the video, and transcripts, captions, comments, and reactions may not be aligned if more edits are made post conversion.\n\nDo you want to continue and add clips to your video?';

    if (window.confirm(confirmAddClipsTitle)) {
      dispatch(updateShowWeaveEditTools());
      onEnterTrimMode();
    }
  };

  const handleOverlayButtonClick = useCallback(() => {
    if (areViewerInsightsOpen) {
      dispatch(toggleViewerInsightsUI());
    }

    if (areVideoSettingsOpen) {
      dispatch(endVideoSettingsFlow());
    }

    if (areVideoCtaSettingsOpen) {
      dispatch(clearCTAWidgetState());
    }

    if (areThumbnailSettingsOpen) {
      dispatch(endThumbnailFlow());
    }
  }, [
    dispatch,
    areViewerInsightsOpen,
    areVideoSettingsOpen,
    areVideoCtaSettingsOpen,
    areThumbnailSettingsOpen,
  ]);

  return (
    <VideoButtons
      startCollapsed={startCollapsed}
      isCollapsible={isCollapsible}
      isHidden={isHidden}
    >
      {/* VideoButtons uses the Function As Child Component (FaCC) pattern */}
      {({ isCollapsed }) => (
        <div className="p:xsmall">
          <TranscriptOverlayButton
            isCollapsed={isCollapsed}
            onOverlayButtonClick={handleOverlayButtonClick}
          />
          <Scopes name={VIDEO_SETTINGS_ACCESS}>
            <CollapsibleSideButton
              onClick={() => {
                if (areViewerInsightsOpen) {
                  dispatch(toggleViewerInsightsUI());
                }

                if (areVideoCtaSettingsOpen) {
                  dispatch(clearCTAWidgetState());
                }

                if (areThumbnailSettingsOpen) {
                  dispatch(endThumbnailFlow());
                }

                if (isTranscriptVisible) {
                  dispatch(handleToggleTranscriptCreator());
                }

                // we only request leaving full screen if already full screen,
                // otherwise we queue up the request to leave full screen
                if (isVideoPlayerFullScreen) {
                  dispatch(requestVideoPlayerLeaveFullScreen());
                  dispatch(startVideoSettingsFlow());
                } else {
                  dispatch(
                    areVideoSettingsOpen
                      ? endVideoSettingsFlow()
                      : startVideoSettingsFlow()
                  );
                }

                dispatch(updateTheaterMode(false));
              }}
              icon={<SvgTune style={{ transform: 'rotate(90deg)' }} />}
              isCollapsed={isCollapsed}
            >
              Video preferences
            </CollapsibleSideButton>
          </Scopes>
          <Scopes name={VIDEO_EDIT_OPTIONS_ACCESS}>
            <>
              {/* if user is in the feature flag and the video is not a V6 video then show Trim button and then an Add Clips seperate button under it */}
              {rolloutWeave && [V3, V4, V5].includes(recordingVersion) ? (
                <>
                  <TrimModeButton
                    onClick={onEnterTrimMode}
                    isCollapsed={isCollapsed}
                    customLabel={`(${VIDEO_ENTER_TRIM_MODE[0]}) Trim video`}
                  />
                  <TrimModeButton
                    onClick={confirmAddClips}
                    isCollapsed={isCollapsed}
                    customLabel={'Add clips'}
                  />
                </>
              ) : (
                <TrimModeButton
                  onClick={onEnterTrimMode}
                  isCollapsed={isCollapsed}
                />
              )}

              {!waitingForTrim && (
                <>
                  <AddVideoCtaButton
                    isCollapsed={isCollapsed}
                    isTranscriptVisible={isTranscriptVisible}
                  />
                  <CustomThumbnailButton
                    isCollapsed={isCollapsed}
                    isTranscriptVisible={isTranscriptVisible}
                  />
                </>
              )}
            </>
          </Scopes>
        </div>
      )}
    </VideoButtons>
  );
};

export default EditVideoButtons;
